// Project-action registry — module-level singleton.
//
// BaseDashboard ships generic verbs (reset / delete / undo / rotate / camera-
// toggle / measurements-toggle / help / open-cart / back-to-series / send-email-
// from-cart). Projects (Main, ACME-webshop, …) call `registerAction()` at
// startup to add their own — submit-to-webshop, submit-bc-order, apply-coupon.
//
// The host (SceneDesignerTool) merges:
//   1. Host-seeded actions (computed each render against current scene state)
//   2. Project-registered actions (this registry, materialised against the
//      host API each render so their `enabled` / `active` predicates see live
//      state)
//
// See .design.md "Project-specific extensions" for why this is the one
// extension hook we ship in v1.

import type { ActionDef, ActionHostApi, ActionRegistration } from "./types";

const registered = new Map<string, ActionRegistration>();

/**
 * Register a project-specific action. Idempotent — re-registering the same id
 * replaces the previous entry, which is the right behavior for hot reload.
 * BaseDashboard built-in ids are protected: they live in the host's seed pass
 * and overwrite anything registered here with the same id. That keeps the
 * generic-vs-specific distinction clean.
 */
export function registerAction(action: ActionRegistration): void {
  if (!action || !action.id) return;
  registered.set(action.id, action);
}

/** Inverse of registerAction — for tests / hot-reload scenarios. */
export function unregisterAction(id: string): void {
  registered.delete(id);
}

/**
 * Snapshot the registered actions against the current host API. Called by the
 * host on each render — produces a plain ActionDef[] the host then merges
 * with its built-ins.
 */
export function materializeRegistered(host: ActionHostApi): ActionDef[] {
  const out: ActionDef[] = [];
  for (const r of registered.values()) {
    out.push({
      id: r.id,
      label: r.label,
      icon: r.icon,
      run: r.run ? () => r.run!(host) : undefined,
      opensModal: r.opensModal,
      enabled: r.enabled ? r.enabled(host) : true,
      active: r.active ? r.active(host) : false,
    });
  }
  return out;
}

/** Test-only: clear all registered actions. */
export function _clearRegisteredActionsForTests(): void {
  registered.clear();
}
